<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'fal fa-clinic-medical'"
      :section-title="$tc('configuration.sections.department', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteDepartments(selectedRows)"
        @shortkey="deleteDepartments(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showDepartmentsModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showDepartmentsModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showDepartmentsModal($t('configuration.actions.add'))"
        @shortkey="showDepartmentsModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="departmentsModalWrapper">
      <departments-configuration-modal
        :action-title="actionTitle"
        @update="patchDepartments($event)"
        @create="createDepartments($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import ConfigurationDepartmentsGridColumns from '@/modules/configurationModule/detail/centersAndServices/departments/ConfigurationDepartmentsGridColumns'
import DepartmentsConfigurationModal from '@/modules/configurationModule/detail/centersAndServices/departments/DepartmentsConfigurationModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import ConfigurationDepartmentServices
    from '@/api/resources/configuration/centersAndAreas/department/ConfigurationDepartmentServices'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'

export default {
    name: 'DepartmentsConfigurationGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        DepartmentsConfigurationModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.centersAndServices.departments',
            actionTitle: null,
            selectedRows: [],
            columnDefs: ConfigurationDepartmentsGridColumns(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveDepartment() {
            return !!this.selectedRows.find(department => department.active)
        },
        hasDeactivatedDepartment() {
            return !!this.selectedRows.find(department => !department.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(department => department.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(department => !department.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveDepartment && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactivatedDepartment && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const departmentsToPatch = selectionBatch.filter(department => department.active !== option).map(department => {
                return {
                    active: option,
                    id: department.id
                }
            })
            if (departmentsToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(ConfigurationDepartmentServices.patchDepartmentsBatch(departmentsToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                    return
                }
                if (selectionBatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: departmentsToPatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchDepartments(...departments) {
            const request = departments.map(department => this.mapDepartmentToRequest(department))
            this.updateLoading(true)
            const [err, data] = await this.toCall(ConfigurationDepartmentServices.patchDepartmentsBatch(request))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.$refs.departmentsModalWrapper.hide()
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
            this.reloadData()
        },
        async createDepartments(department) {
            const request = this.mapDepartmentToRequest(department)
            this.updateLoading(true)
            const [err] = await this.toCall(ConfigurationDepartmentServices.createDepartment(request))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            this.$refs.departmentsModalWrapper.hide()
            this.showToastSuccessGlobal(this.$t('configuration.toastMessages.created'))
            this.reloadData()
        },
        async deleteDepartmentsBatch(departments) {
            const departmentsIds = departments.map(area => area.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(ConfigurationDepartmentServices.deleteDepartmentsBatch(departmentsIds))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (departmentsIds.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: departmentsIds.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        async deleteDepartments(departments) {
            if (departments.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteDepartmentsBatch(departments)
                }
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(ConfigurationDepartmentServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showDepartmentsModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteDepartments(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(ConfigurationDepartmentServices))
        },
        showDepartmentsModal(title, selectedDepartment) {
            this.actionTitle = title
            this.$refs.departmentsModalWrapper.show([selectedDepartment])
        },
        mapDepartmentToRequest(department) {
            return {
                id: department.id,
                active: department.active,
                centerId: department.center.id,
                code: department.code,
                contactInformation: department.contactInformation,
                departmentTypeId: department.departmentType.id,
                name: department.name
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
